import { useSyncExternalStore } from 'react';
import { DEFAULT_SETTINGS } from '@core/settings/schema.ts';
import type { SettingsStore } from '@ports/SettingsStore.ts';
import type { SubtitleStore, SubtitleSlot } from '../state/subtitleStore.ts';
import type { UseSubtitleLoaderResult } from '../hooks/useSubtitleLoader.ts';
import {
  findTranslationByCode,
  orderTranslationsByMru,
  rememberLanguagePreference,
} from '../lib/trackPreference.ts';
import { maybeApplyLanguageStyleRuntime } from '../lib/applyLanguageStyleRuntime.ts';
import { useI18n } from '../i18n/index.ts';
import { SheetHandle } from './SheetHandle.tsx';

export type TrackSelectorProps = {
  store: SubtitleStore;
  settingsStore: SettingsStore;
  loader: UseSubtitleLoaderResult;
  unlock?: boolean;
  onRequestLogin?: () => void;
  open: boolean;
  onClose: () => void;
};

function isMemberOnlyTrack(track: {
  memberOnly?: boolean;
  enhanced?: boolean;
  whisper?: boolean;
} | undefined): boolean {
  return Boolean(track?.memberOnly || track?.whisper);
}

/** Extension popup: subtitle track select (mainSubtitle / secondSubtitle). */
function SubtitleMenu({
  slot,
  store,
  loader,
  settingsStore,
  unlock,
  onRequestLogin,
  testId,
}: {
  slot: SubtitleSlot;
  store: SubtitleStore;
  loader: UseSubtitleLoaderResult;
  settingsStore: SettingsStore;
  unlock: boolean;
  onRequestLogin?: () => void;
  testId: string;
}) {
  const { t } = useI18n();
  const state = useSyncExternalStore(
    (cb) => store.subscribe(cb),
    () => store.getSlot(slot),
  );
  const tracks = state.trackList?.subtitles ?? [];

  return (
    <select
      className="field track-selector__menu"
      data-testid={testId}
      aria-label={slot === 'main' ? t('trackSlotMain') : t('trackSlotSecond')}
      value={state.selectedId ?? ''}
      disabled={tracks.length === 0 || state.status === 'loading'}
      onChange={(e) => {
        const value = e.target.value;
        if (!value) {
          store.select(slot, undefined);
          store.setCues(slot, undefined);
          store.setStatus(slot, 'idle');
          store.setViewState(slot, {
            hidden: true,
            dualMode: false,
          });
          const langKey = slot === 'main' ? 'mainLanguages' : 'secondLanguages';
          // Extension saveSelected: insertLanguage(..., 'null'); translation MRU untouched.
          const settings = settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS;
          const mru = slot === 'main' ? settings.mainLanguages : settings.secondLanguages;
          void settingsStore.set({
            [langKey]: rememberLanguagePreference(mru, 'null'),
          });
          return;
        }
        const track = tracks.find((tr) => String(tr.value) === value) as
          | { memberOnly?: boolean; enhanced?: boolean; whisper?: boolean }
          | undefined;
        if (isMemberOnlyTrack(track) && !unlock) {
          onRequestLogin?.();
        }
        store.setViewState(slot, { hidden: false });
        void loader.selectTrack(slot, value);
      }}
    >
      <option value="">—</option>
      {tracks.map((track) => {
        const locked = isMemberOnlyTrack(
          track as { memberOnly?: boolean; enhanced?: boolean; whisper?: boolean },
        );
        return (
          <option key={track.value} value={String(track.value)}>
            {track.name}
            {locked && !unlock ? ` (${t('memberTrackLabel')})` : ''}
          </option>
        );
      })}
    </select>
  );
}

/** Extension popup: translation language select (mainTranslation / secondTranslation). */
function TranslationMenu({
  slot,
  store,
  settingsStore,
  testId,
}: {
  slot: SubtitleSlot;
  store: SubtitleStore;
  settingsStore: SettingsStore;
  testId: string;
}) {
  const { t } = useI18n();
  const state = useSyncExternalStore(
    (cb) => store.subscribe(cb),
    () => store.getSlot(slot),
  );
  const settings = useSyncExternalStore(
    (cb) => settingsStore.subscribe(cb),
    () => settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS,
  );
  const mruKey = slot === 'main' ? 'mainTranslations' : 'secondTranslations';
  const translations = orderTranslationsByMru(
    state.trackList?.translations ?? [],
    settings[mruKey],
  );
  const targetCode = settings[mruKey][0] ?? '';
  // Extension popup: when paired subtitle is "—" / null, leave translation <select> empty
  // (no language options). Do not show MRU target while no source track is selected.
  const hasSubtitle = Boolean(state.selectedId);
  const selectedValue = hasSubtitle
    ? findTranslationByCode(translations, targetCode)?.value
    : undefined;
  const showTranslationOptions = hasSubtitle && translations.length > 0;

  return (
    <select
      className="field track-selector__menu"
      data-testid={testId}
      aria-label={slot === 'main' ? t('trackTranslateMain') : t('trackTranslateSecond')}
      value={selectedValue !== undefined ? String(selectedValue) : ''}
      disabled={!showTranslationOptions}
      onChange={(e) => {
        const value = e.target.value;
        const currentMru = settings[mruKey];
        if (!value) {
          // Extension saveSelected translation off → insertLanguage(..., 'null').
          void settingsStore.set({
            [mruKey]: rememberLanguagePreference(currentMru, 'null'),
          });
          store.setViewState(slot, { dualMode: false });
          const cues = store.getSlot(slot).cues;
          if (cues?.length) {
            for (const cue of cues) {
              cue.translated = false;
              cue.translation = undefined;
              cue.loading = false;
            }
          }
          // Fall back to subtitle-language style binding for this slot.
          const selectedId = store.getSlot(slot).selectedId;
          const subCode = store
            .getSlot(slot)
            .trackList?.subtitles.find((t) => String(t.value) === selectedId)?.code;
          maybeApplyLanguageStyleRuntime(settingsStore, slot, subCode);
          return;
        }
        const row = translations.find((tr) => String(tr.value) === value);
        const code = row?.code ?? '';
        const cues = store.getSlot(slot).cues;
        if (cues?.length) {
          for (const cue of cues) {
            cue.translated = false;
            cue.translation = undefined;
            cue.loading = false;
          }
        }
        // Extension saveSelected: insertLanguage + drop null; does not auto-enable dualMode.
        if (!code) return;
        void settingsStore.set({
          [mruKey]: rememberLanguagePreference(currentMru, code),
        });
        store.setViewState(slot, { hidden: false });
        maybeApplyLanguageStyleRuntime(settingsStore, slot, code);
      }}
    >
      {showTranslationOptions ? (
        <>
          <option value="">—</option>
          {translations.map((tr) => (
            <option key={tr.value} value={String(tr.value)}>
              {tr.name}
            </option>
          ))}
        </>
      ) : null}
    </select>
  );
}

function FieldLabel({ children }: { children: string }) {
  return <span className="track-selector__field-label">{children}</span>;
}

/**
 * Extension popup Subtitles tab: four selects.
 * Import / export / engine live in Settings (extension Settings tab).
 */
export function TrackSelector({
  store,
  settingsStore,
  loader,
  unlock = false,
  onRequestLogin,
  open,
  onClose,
}: TrackSelectorProps) {
  const { t } = useI18n();

  if (!open) return null;

  return (
    <aside className="track-selector panel-sheet" data-testid="track-selector" aria-label={t('tracks')}>
      <SheetHandle onDismiss={onClose} />
      <div className="track-selector__header">
        <h2>{t('tracks')}</h2>
        <button
          type="button"
          className="track-selector__close btn"
          onClick={onClose}
          aria-label={t('settingsClose')}
        >
          {t('settingsClose')}
        </button>
      </div>

      <div className="track-selector__menus track-selector__menus--ext" data-testid="track-menus">
        <div className="track-selector__field">
          <FieldLabel>{t('trackSlotMain')}</FieldLabel>
          <SubtitleMenu
            slot="main"
            store={store}
            loader={loader}
            settingsStore={settingsStore}
            unlock={unlock}
            onRequestLogin={onRequestLogin}
            testId="track-select-main"
          />
        </div>
        <div className="track-selector__field">
          <FieldLabel>{t('trackTranslateMain')}</FieldLabel>
          <TranslationMenu
            slot="main"
            store={store}
            settingsStore={settingsStore}
            testId="track-translate-main"
          />
        </div>

        <div className="track-selector__field">
          <FieldLabel>{t('trackSlotSecond')}</FieldLabel>
          <SubtitleMenu
            slot="second"
            store={store}
            loader={loader}
            settingsStore={settingsStore}
            unlock={unlock}
            onRequestLogin={onRequestLogin}
            testId="track-select-second"
          />
        </div>
        <div className="track-selector__field">
          <FieldLabel>{t('trackTranslateSecond')}</FieldLabel>
          <TranslationMenu
            slot="second"
            store={store}
            settingsStore={settingsStore}
            testId="track-translate-second"
          />
        </div>
      </div>
    </aside>
  );
}
